import {
  EARLY_ACCESS,
  INSURANCE_RATES,
  PLATFORM_FEES,
  TAX_RATES,
  LATE_RETURN,
  type InsuranceTier,
} from './constants'
import {
  calculateExtraKmCharge,
  calculateLateFeeCore,
  calculateRefundCore,
  calculateTripPriceCore,
  type RefundBreakdown,
  type RefundParams,
  type TripPriceCoreBreakdown,
} from './pricing-core'

export interface TripPriceParams {
  dailyRateCents: number
  days: number
  insuranceTier: InsuranceTier
  promoDiscountCents?: number
  isSuperhost?: boolean
  /** Optional host-set security deposit (refundable hold, added untaxed). */
  depositCents?: number
  /** Vehicle's quoted daily premium; wins over the flat constant fallback. */
  insuranceDailyRateCents?: number | null
  guestServiceFeePct?: number
}

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * EL DEPOSITO NO PUEDE SER ALGO QUE HAYA QUE ACORDARSE DE PASAR.
 *
 * `calculateTripPrice` acepta `depositCents` y `promoDiscountCents` como
 * opcionales, asi que cada pantalla decidia que incluir. Y decidieron distinto:
 *
 *   app/booking/[vehicleId].tsx   tarifa + comision                 (sin promo, SIN deposito)
 *   app/booking/checkout.tsx      tarifa + comision + promo + deposito
 *   lib/services/booking.service  tarifa + comision + promo + deposito   <- el que cobra
 *
 * Las dos primeras pantallas se llaman "Total" en la interfaz. La persona leia
 * Total $74.75, tocaba Continuar, y leia Total $574.75 sin haber cambiado nada.
 *
 * Pasar el parametro que falta habria arreglado ESA pantalla y dejado el filo
 * intacto para la siguiente. El defecto no es la llamada: es que la firma
 * PERMITA una vista parcial del precio de un vehiculo.
 *
 * Por eso el precio de un viaje sobre un vehiculo concreto se calcula con
 * `tripPriceForVehicle`, donde el deposito NO es un parametro que el llamante
 * suministra --sale del vehiculo-- y por lo tanto no se puede omitir. Escribir
 * la llamada incorrecta deja de ser posible, que es mas fuerte que acordarse de
 * escribir la correcta.
 *
 * `calculateTripPrice` se queda como primitiva de bajo nivel para lo que de
 * verdad trabaja con numeros sueltos (estimaciones, pruebas). Ninguna pantalla
 * deberia llamarla directo, y `validate-one-price.mjs` lo comprueba.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Lo minimo de un vehiculo que hace falta para saber cuanto se le cobra a alguien. */
export interface PricedVehicle {
  dailyRateCents: number
  /** El deposito opcional del host. Nulo y cero son lo mismo aca. */
  securityDepositCents?: number | null
  /**
   * Prima diaria de cobertura cotizada con el asegurador (passthrough 1:1).
   * Nulo = el vehiculo aun no tiene cotizacion; el core usa el fallback.
   */
  coverageDailyRateCents?: number | null
  /**
   * Whether this vehicle's trip carries platform (Qualitas) coverage. `false`
   * (own-policy / exempt / org) always zeroes the premium line; the trip is
   * covered by the listing's own policy and there is no platform premium to
   * charge or declare. `true`/undefined keep the quote-or-fallback behavior.
   * The server derives the same flag from the vehicle columns shared by
   * `vehicleHasPlatformCoverage`.
   */
  hasPlatformCoverage?: boolean | null
}

/** Lo que la PERSONA eligio. Deliberadamente no incluye el deposito. */
export interface TripSelection {
  days: number
  insuranceTier: InsuranceTier
  promoDiscountCents?: number
  isSuperhost?: boolean
  /** Pair-repeat take rate. Omit to use the 15% first-match default. */
  guestServiceFeePct?: number
}

/**
 * El precio de un viaje sobre un vehiculo. Una sola forma, para todas las
 * pantallas y para el servicio que cobra.
 */
export function tripPriceForVehicle(
  vehicle: PricedVehicle,
  trip: TripSelection,
): TripPriceBreakdown {
  return calculateTripPrice({
    dailyRateCents: vehicle.dailyRateCents,
    days: trip.days,
    insuranceTier: trip.insuranceTier,
    promoDiscountCents: trip.promoDiscountCents,
    isSuperhost: trip.isSuperhost,
    guestServiceFeePct: trip.guestServiceFeePct,
    // DESACOPLE DE LA PRIMA (P3): un vehículo sin cobertura de plataforma no
    // lleva prima, ni cotizada ni fallback — su póliza de lista cubre el viaje.
    // El `false` explícito es lo que alinea esta línea con el cargo
    // autoritativo (`insuranceCents` cero del lado del servidor).
    insuranceDailyRateCents:
      vehicle.hasPlatformCoverage === false ? 0 : (vehicle.coverageDailyRateCents ?? null),
    // Del vehiculo, siempre. Es la linea entera del arreglo.
    depositCents: Math.max(0, vehicle.securityDepositCents ?? 0),
  })
}

export type TripPriceBreakdown = TripPriceCoreBreakdown

export function calculateTripPrice(params: TripPriceParams): TripPriceBreakdown {
  return calculateTripPriceCore(params, {
    platformInsuranceEnabled: EARLY_ACCESS.platformInsuranceEnabled,
    insuranceRates: INSURANCE_RATES,
    guestServiceFeePct: PLATFORM_FEES.guestServiceFeePct,
    superhostFeePct: PLATFORM_FEES.superhostFeePct,
    ivaPct: TAX_RATES.ivaPct,
    depositProcessingFeePct: PLATFORM_FEES.depositProcessingFeePct,
    guestServiceFeeProcessingPct: PLATFORM_FEES.guestServiceFeeProcessingPct,
  })
}

export type { RefundBreakdown, RefundParams }

export function calculateRefund(params: RefundParams): RefundBreakdown {
  return calculateRefundCore(params)
}

export function calculateLateFee(dailyRateCents: number, minutesLate: number): number {
  return calculateLateFeeCore(dailyRateCents, minutesLate, LATE_RETURN)
}

export { calculateExtraKmCharge }

/**
 * Quick estimate of a one-day checkout total. Kept for tests and internal
 * comparisons, but not used as the public sticker price while consumer-final
 * totals are shown only in checkout.
 */
export function calculateAllInDailyCents(dailyRateCents: number): number {
  const breakdown = calculateTripPrice({
    dailyRateCents,
    days: 1,
    insuranceTier: 'platform',
  })
  return breakdown.totalCents
}

/**
 * The canonical "sticker price" a guest sees before checkout: raw base rate
 * only. Consumer-final totals still go through `calculateTripPrice` and are
 * shown at the payment/checkout step.
 *
 * Change this function's body to flip the sticker across the whole app
 * in one edit:
 * Platform-fee / IVA edits go in `lib/utils/constants.ts` and affect only the
 * checkout total.
 *
 * Hourly-rental categories (`heavy_duty`) pass through their
 * `hourlyRateCents` unchanged — the all-in math is a daily-rental
 * concept and would fold hours into days nonsensically.
 */
export function getVehicleDisplayPriceCents(vehicle: {
  category?: string | null
  dailyRateCents: number
  hourlyRateCents?: number | null
}): { cents: number; unit: 'day' | 'hour' } {
  const isHourly = vehicle.category === 'heavy_duty'
  if (isHourly && vehicle.hourlyRateCents) {
    return { cents: vehicle.hourlyRateCents, unit: 'hour' }
  }
  return {
    cents: vehicle.dailyRateCents,
    unit: 'day',
  }
}
